import { useQuery, UseQueryResult } from "@tanstack/react-query";

import { useDomainServices } from "@/providers/DomainServices";

import { AgentThreadArtifactUrlResponse } from "../AgentThreadRecord";

/**
 * Max age of a cached presigned URL a mounting component accepts. Must stay well under the
 * backend's 5-minute URL TTL (`DOWNLOAD_URL_TTL_S`) so a cached URL still has time to load.
 */
export const ARTIFACT_URL_STALE_MS = 60_000;

/** Unobserved URLs are cheap to re-mint, so keep them no longer than they are fresh. */
const ARTIFACT_URL_GC_MS = ARTIFACT_URL_STALE_MS;

export function getArtifactUrlQueryKey(
  threadId?: string,
  toolUseId?: string,
  artifactName?: string,
) {
  if (
    threadId === undefined ||
    toolUseId === undefined ||
    artifactName === undefined
  ) {
    return ["agentThreadArtifactUrl"];
  }

  return ["agentThreadArtifactUrl", threadId, toolUseId, artifactName];
}

/**
 * Fetch a short-TTL presigned URL for rendering one tool-produced artifact in place (an `<img>`
 * can't send the API's auth header). No refetch on focus, which would only reload the image; the
 * caller refetches if the image fails to load. Downloads use `useDownloadArtifact` instead.
 */
export function useArtifactUrl(
  threadId: string | undefined,
  toolUseId: string | undefined,
  artifactName: string | undefined,
): UseQueryResult<AgentThreadArtifactUrlResponse, Error> {
  const { agentThreadService } = useDomainServices();

  return useQuery<AgentThreadArtifactUrlResponse, Error>({
    queryKey: getArtifactUrlQueryKey(threadId, toolUseId, artifactName),
    queryFn: ({ signal }) => {
      if (
        threadId === undefined ||
        toolUseId === undefined ||
        artifactName === undefined
      ) {
        return Promise.reject(
          new Error("Invalid threadId, toolUseId, or artifactName"),
        );
      }

      return agentThreadService.getArtifactUrl(
        threadId,
        toolUseId,
        artifactName,
        { abortSignal: signal },
      );
    },
    enabled:
      threadId !== undefined &&
      toolUseId !== undefined &&
      artifactName !== undefined,
    staleTime: ARTIFACT_URL_STALE_MS,
    gcTime: ARTIFACT_URL_GC_MS,
    refetchOnWindowFocus: false,
  });
}
